// Dependency-graph staleness check + confirm-then-chain-run flow. Triggered
// from sidebar.ts whenever the user picks a dataset; if the target dataset's
// output is stale relative to an upstream module's output (per
// module_dependencies.py's DAG), shows a brief confirmation before
// automatically re-running every stale module in dependency order — matches
// the user's explicit choice of "confirm first" over a silent auto-run,
// given some of these re-runs can take several minutes.
import { getStaleness, runModuleAndWait } from "./api";
import { el } from "./legend";
import { withLoading } from "./loading";
import type { AuditLogEntry, StalenessModule } from "./types";

export function overlay(): HTMLElement {
  const back = el("div");
  back.style.position = "fixed";
  back.style.inset = "0";
  back.style.background = "rgba(0,0,0,0.6)";
  back.style.display = "flex";
  back.style.alignItems = "center";
  back.style.justifyContent = "center";
  back.style.zIndex = "1000";
  return back;
}

export function modalBox(): HTMLElement {
  const box = el("div");
  box.style.background = "var(--panel-bg, #171b24)";
  box.style.border = "1px solid var(--border, #2a2f3a)";
  box.style.borderRadius = "6px";
  box.style.padding = "20px";
  box.style.maxWidth = "480px";
  box.style.width = "90%";
  box.style.maxHeight = "80vh";
  box.style.overflowY = "auto";
  return box;
}

/** One line per changed field: "eps_m: 400 -> 999". Falls back to a bare
 * path if old/new aren't simple scalars (e.g. a table/list diff) — this is
 * a quick "what changed" hint, not a full diff viewer. */
function formatChange(change: { path: string; old: unknown; new: unknown }): string {
  const fmt = (v: unknown) => (typeof v === "object" ? JSON.stringify(v) : String(v));
  return `${change.path}: ${fmt(change.old)} → ${fmt(change.new)}`;
}

/** Admin-only "who changed what" — see routers/datasets.py's get_staleness,
 * which only ever populates this for an admin (a configure/view user's
 * modules_to_run is either empty or just their own module, nothing to
 * explain). Rendered as a collapsible <details> so it doesn't dominate the
 * modal when there's nothing actionable to do but re-run. */
function renderRecentChanges(box: HTMLElement, recentChanges: AuditLogEntry[]): void {
  if (recentChanges.length === 0) return;
  const details = document.createElement("details");
  details.style.marginTop = "12px";
  details.style.fontSize = "12px";
  const summary = document.createElement("summary");
  summary.textContent = "Recent config changes that may explain this";
  details.append(summary);
  const list = document.createElement("ul");
  for (const entry of recentChanges) {
    const who = entry.display_name ?? entry.username ?? "unknown user";
    const when = new Date(entry.at).toLocaleString();
    for (const change of entry.detail.changes ?? []) {
      const li = document.createElement("li");
      li.textContent = `${entry.target_id} — ${formatChange(change)} (${who}, ${when})`;
      list.append(li);
    }
  }
  details.append(list);
  box.append(details);
}

/** Resolves true if the user confirms, false if they cancel. */
function showConfirmModal(modules: StalenessModule[], recentChanges: AuditLogEntry[]): Promise<boolean> {
  return new Promise((resolve) => {
    const back = overlay();
    const box = modalBox();
    box.append(
      el("h2", {}, "Some data is out of date"),
      el(
        "p",
        {},
        `${modules.length} module(s) need to be re-run before this dataset reflects the current ` +
          "upstream data:",
      ),
    );
    const list = document.createElement("ul");
    for (const m of modules) {
      const li = document.createElement("li");
      li.textContent = `Module ${m.module} (${m.script})${m.runtime_note ? ` — approx. ${m.runtime_note}` : " — duration unknown"}`;
      list.append(li);
    }
    box.append(list);
    renderRecentChanges(box, recentChanges);

    const btnRow = el("div");
    btnRow.style.display = "flex";
    btnRow.style.gap = "8px";
    btnRow.style.justifyContent = "flex-end";
    const cancelBtn = el("button", {}, "Cancel");
    const runBtn = el("button", {}, "Re-run now");
    cancelBtn.addEventListener("click", () => {
      back.remove();
      resolve(false);
    });
    runBtn.addEventListener("click", () => {
      back.remove();
      resolve(true);
    });
    btnRow.append(cancelBtn, runBtn);
    box.append(btnRow);

    back.append(box);
    document.body.append(back);
  });
}

/** Runs every module in order, showing live progress + log. Resolves true if
 * all succeeded, false if any failed (caller decides whether to still allow
 * the dataset switch — see staleness.ts's usage in sidebar.ts, which does
 * not proceed on failure). */
function runChainWithProgress(modules: StalenessModule[]): Promise<boolean> {
  return new Promise((resolve) => {
    const back = overlay();
    const box = modalBox();
    box.append(el("h2", {}, "Re-running stale modules…"));

    const statusRows = new Map<string, HTMLElement>();
    for (const m of modules) {
      const row = el("div", {}, `⏳ Module ${m.module} (${m.script}) — pending`);
      statusRows.set(m.module, row);
      box.append(row);
    }

    const logEl = document.createElement("pre");
    logEl.style.maxHeight = "200px";
    logEl.style.overflowY = "auto";
    logEl.style.fontSize = "11px";
    logEl.style.marginTop = "12px";
    box.append(logEl);

    back.append(box);
    document.body.append(back);

    (async () => {
      for (const m of modules) {
        const row = statusRows.get(m.module)!;
        row.textContent = `🔄 Module ${m.module} (${m.script}) — running…`;
        const result = await runModuleAndWait(m.module, "full", (line) => {
          logEl.textContent += line + "\n";
          logEl.scrollTop = logEl.scrollHeight;
        });
        if (result.status === "complete" && result.returncode === 0) {
          row.textContent = `✅ Module ${m.module} (${m.script}) — done`;
        } else {
          row.textContent = `❌ Module ${m.module} (${m.script}) — failed (${result.status}, exit ${result.returncode ?? "?"})`;
          const closeBtn = el("button", {}, "Close");
          closeBtn.addEventListener("click", () => {
            back.remove();
            resolve(false);
          });
          box.append(closeBtn);
          return;
        }
      }
      back.remove();
      resolve(true);
    })();
  });
}

/** Checks staleness for targetDataset and, if stale, walks the user through
 * confirm -> chain-run before returning. Returns true if the caller should
 * proceed with switching to targetDataset (either it was already fresh, or
 * the fix-chain completed successfully); false if the user cancelled or a
 * re-run failed, in which case the caller should NOT switch (data would
 * still be inconsistent). */
export async function ensureFreshBeforeSwitch(targetDataset: string): Promise<boolean> {
  let staleness;
  try {
    staleness = await withLoading(() => getStaleness(targetDataset));
  } catch (err) {
    console.error("Staleness check failed, proceeding without it:", err);
    return true; // don't block the user on a broken staleness check
  }
  if (!staleness.stale) return true;

  const confirmed = await showConfirmModal(staleness.modules_to_run, staleness.recent_changes);
  if (!confirmed) return false;

  return runChainWithProgress(staleness.modules_to_run);
}
